import { validateImportReviewPayload } from './appPayloadReview';

export const CHARACTER_IMPORT_UPLOAD_LIMIT_BYTES = 5 * 1024 * 1024;

const PREVIEW_ENVELOPE_KEYS = new Set([
  'ok',
  'requestId',
  'source',
  'mode',
  'file',
  'payload',
  'summary',
  'detected',
  'error',
  'fallback',
]);
const SAFE_PREVIEW_ERROR_MESSAGES = {
  'file-too-large': 'The uploaded PDF is too large for character import.',
  'invalid-pdf': 'The uploaded file is not a supported PDF.',
  'unsupported-pdf-structure': 'This PDF structure is not supported by character import.',
  'ddb-shape-validation-failed': 'This PDF does not look like a supported DDB character sheet.',
  'ocr-import-not-ready': 'This looks like a scanned or image-based sheet. OCR import is not available yet.',
  'unsupported-character-sheet': 'This PDF does not look like a supported DDB or DiceCloud character sheet.',
  'srd-catalog-unavailable': 'The SRD catalog is unavailable for character import.',
  'import-preview-failed': 'Character import preview failed.',
};

// The generic auto-detected upload id. The classifier on the server picks the
// reader; the reviewed payload's receipt declares what was detected.
export const CHARACTER_PDF_UPLOAD_SOURCE = 'character-pdf';

function isObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function safeErrorMessage(error) {
  if (typeof error?.code === 'string' && SAFE_PREVIEW_ERROR_MESSAGES[error.code]) {
    return SAFE_PREVIEW_ERROR_MESSAGES[error.code];
  }
  return SAFE_PREVIEW_ERROR_MESSAGES['import-preview-failed'];
}

function assertSupportedUploadRequest({ file, source, mode }) {
  if (source !== CHARACTER_PDF_UPLOAD_SOURCE && source !== 'shenyi') {
    throw new Error('Production PDF upload is only available for supported character sheet imports.');
  }
  if (mode !== 'create' && mode !== 'update') {
    throw new Error('Production PDF upload is only enabled for create or update import reviews.');
  }
  if (!file || typeof file !== 'object') {
    throw new Error('Choose a PDF file before uploading.');
  }
  if (typeof file.size === 'number' && file.size > CHARACTER_IMPORT_UPLOAD_LIMIT_BYTES) {
    throw new Error('The selected PDF is too large for character import.');
  }
  const fileName = typeof file.name === 'string' ? file.name : '';
  const fileType = typeof file.type === 'string' ? file.type : '';
  if (fileType && fileType !== 'application/pdf') {
    throw new Error('Choose a PDF file before uploading.');
  }
  if (fileName && !/\.pdf$/i.test(fileName)) {
    throw new Error('Choose a PDF file before uploading.');
  }
}

export function unwrapCharacterImportPreviewEnvelope(envelope, expected = {}) {
  if (!isObject(envelope)) {
    throw new Error('Character import preview did not return a valid response.');
  }

  for (const key of Object.keys(envelope)) {
    if (!PREVIEW_ENVELOPE_KEYS.has(key)) {
      throw new Error('Character import preview returned unsafe response fields.');
    }
  }

  if (envelope.ok === false) {
    throw new Error(safeErrorMessage(envelope.error));
  }
  if (envelope.ok !== true) {
    throw new Error('Character import preview did not complete.');
  }
  if (expected.source && envelope.source && envelope.source !== expected.source) {
    throw new Error('Character import preview source mismatch.');
  }
  if (expected.mode && envelope.mode && envelope.mode !== expected.mode) {
    throw new Error('Character import preview mode mismatch.');
  }

  // Auto-detected uploads don't pin an expected payload source kind — the
  // server's classifier decides, and validateImportReviewPayload's enabled-
  // source-kind allowlist still applies. Persona sources keep the exact match.
  const expectPinnedSource = expected.source && expected.source !== CHARACTER_PDF_UPLOAD_SOURCE;
  const safePayload = validateImportReviewPayload(
    envelope.payload,
    expectPinnedSource ? { expectedSource: expected.source } : {}
  );
  if (expected.mode && safePayload.mode !== expected.mode) {
    throw new Error('Character import preview payload mode mismatch.');
  }
  return safePayload;
}

export function createCharacterSheetUploadClient({
  createUploadSession,
  registerUpload,
  previewUploadedCharacterPdf = null,
  // Legacy DDB-only action hook; used only when the generic action is absent.
  previewUploadedShenyiPdf = null,
  fetchImpl = typeof fetch === 'function' ? fetch : null,
} = {}) {
  return async function uploadCharacterSheetForReview({
    file,
    source = CHARACTER_PDF_UPLOAD_SOURCE,
    mode = 'create',
    includeEquipment = false,
    includeSpells,
    includeFeatures,
  } = {}) {
    assertSupportedUploadRequest({ file, source, mode });
    const previewAction = source === CHARACTER_PDF_UPLOAD_SOURCE
      ? previewUploadedCharacterPdf
      : previewUploadedShenyiPdf;
    if (typeof createUploadSession !== 'function'
      || typeof registerUpload !== 'function'
      || typeof previewAction !== 'function'
      || typeof fetchImpl !== 'function') {
      throw new Error('Production PDF upload is not available in this context.');
    }

    const options = {
      source,
      mode,
      includeEquipment: includeEquipment === true,
      ...(typeof includeSpells === 'boolean' ? { includeSpells } : {}),
      ...(typeof includeFeatures === 'boolean' ? { includeFeatures } : {}),
    };

    const session = await createUploadSession(options);
    const uploadResponse = await fetchImpl(session.uploadUrl, {
      method: 'POST',
      headers: file.type ? { 'Content-Type': file.type } : undefined,
      body: file,
    });
    if (!uploadResponse?.ok) {
      throw new Error('Character import PDF upload failed.');
    }

    const uploadJson = await uploadResponse.json();
    const storageId = uploadJson?.storageId;
    if (typeof storageId !== 'string' || !storageId.trim()) {
      throw new Error('Character import upload did not return storage metadata.');
    }

    await registerUpload({
      sessionId: session.sessionId,
      storageId,
    });

    const envelope = await previewAction({
      sessionId: session.sessionId,
      storageId,
      ...options,
    });

    return unwrapCharacterImportPreviewEnvelope(envelope, options);
  };
}
